/**
 * Promo modal (homepage)
 * --------------------------------------------------
 * - Appears after a short delay (or on first scroll, whichever is first).
 * - Frequency (hours) controls re-appearance: 0 = show on every page load;
 *   > 0 = show at most once per that window per visitor (localStorage).
 *   Closing it (or clicking the CTA) starts the cooldown.
 * - Dismissible via close button, backdrop click, and ESC.
 * - Focus-trapped while open, restores focus on close, locks body scroll.
 * - Respects prefers-reduced-motion (CSS handles the animation toggle).
 */

const STORAGE_KEY = 'beproPromoModal';
const OPEN_DELAY = 1800; // ms

function readSeen() {
	try {
		const raw = window.localStorage.getItem(STORAGE_KEY);
		return raw ? JSON.parse(raw) : null;
	} catch (e) {
		return null;
	}
}

function markSeen() {
	try {
		window.localStorage.setItem(
			STORAGE_KEY,
			JSON.stringify({ ts: Date.now() })
		);
	} catch (e) {
		/* localStorage unavailable — accept one-shot without memory */
	}
}

function withinCooldown(frequencyHours) {
	const seen = readSeen();
	if (!seen || !seen.ts) return false;
	const windowMs = frequencyHours * 60 * 60 * 1000;
	return Date.now() - seen.ts < windowMs;
}

const FOCUSABLE = [
	'a[href]',
	'button:not([disabled])',
	'textarea',
	'input',
	'select',
	'[tabindex]:not([tabindex="-1"])',
].join(',');

export default function PromoModal() {
	const modal = document.querySelector('[data-promo-modal]');
	if (!modal) return;

	const dialog = modal.querySelector('.promo-modal__dialog');
	const parsedFreq = parseInt(modal.dataset.frequency, 10);
	const frequency = Number.isNaN(parsedFreq) ? 24 : parsedFreq;
	// frequency === 0 → always show on every page load (no cooldown).
	const useCooldown = frequency > 0;

	if (useCooldown && withinCooldown(frequency)) return;

	let lastFocused = null;
	let isOpen = false;

	function getFocusable() {
		return Array.from(dialog.querySelectorAll(FOCUSABLE)).filter(
			(el) => el.offsetParent !== null
		);
	}

	function onKeydown(e) {
		if (e.key === 'Escape') {
			close();
			return;
		}
		if (e.key !== 'Tab') return;

		const items = getFocusable();
		if (!items.length) return;
		const first = items[0];
		const last = items[items.length - 1];

		if (e.shiftKey && document.activeElement === first) {
			e.preventDefault();
			last.focus();
		} else if (!e.shiftKey && document.activeElement === last) {
			e.preventDefault();
			first.focus();
		}
	}

	function open() {
		if (isOpen) return;
		isOpen = true;
		lastFocused = document.activeElement;

		modal.hidden = false;
		// Force reflow so the transition runs from the hidden state.
		void modal.offsetWidth;
		modal.classList.add('is-open');
		document.body.classList.add('promo-modal-open');

		const items = getFocusable();
		if (items.length) {
			items[0].focus();
		} else {
			dialog.setAttribute('tabindex', '-1');
			dialog.focus();
		}

		document.addEventListener('keydown', onKeydown);
	}

	function close() {
		if (!isOpen) return;
		isOpen = false;

		modal.classList.remove('is-open');
		document.body.classList.remove('promo-modal-open');
		document.removeEventListener('keydown', onKeydown);
		if (useCooldown) markSeen();

		const finish = () => {
			modal.hidden = true;
			if (lastFocused && typeof lastFocused.focus === 'function') {
				lastFocused.focus();
			}
			dialog.removeEventListener('transitionend', finish);
		};

		const reduced = window.matchMedia(
			'(prefers-reduced-motion: reduce)'
		).matches;
		if (reduced) {
			finish();
		} else {
			dialog.addEventListener('transitionend', finish);
			// Safety fallback if transitionend never fires.
			window.setTimeout(finish, 400);
		}
	}

	modal.querySelectorAll('[data-promo-close]').forEach((el) => {
		el.addEventListener('click', close);
	});

	// Closing via the CTA still records the cooldown so it won't re-open.
	const cta = modal.querySelector('[data-promo-cta]');
	if (cta && useCooldown) cta.addEventListener('click', markSeen);

	// Trigger: timed delay OR first scroll, whichever happens first.
	let triggered = false;
	const trigger = () => {
		if (triggered) return;
		triggered = true;
		window.removeEventListener('scroll', trigger);
		open();
	};

	window.setTimeout(trigger, OPEN_DELAY);
	window.addEventListener('scroll', trigger, { once: true, passive: true });
}
